/**
 * Queue tab — available (unclaimed) accountant cases.
 * Extracted from pages/AccountantQueue.tsx during Task 4. Pure IA move, no
 * behavior change.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { AccountantTabProps, CaseSummary, QueueResponse } from './types';
import AccountantCaseRow from './AccountantCaseRow';
import TabShell, { StatusFilterValue } from './TabShell';
import { useCaseFilter } from './useCaseFilter';

const VALID_STATUS_FILTERS: StatusFilterValue[] = ['all', 'ready', 'needs-review', 'stale'];

export default function QueueTab({
  selectedCaseId,
  onSelectCase,
  currentAccountantId,
  onVisibleCaseIdsChange,
}: AccountantTabProps) {
  const [cases, setCases] = useState<CaseSummary[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [claimingCase, setClaimingCase] = useState<string | null>(null);

  // Status filter is URL-driven so the dashboard's KPI stat-card buttons
  // can drive it via ?filter=needs-review (BUG-3) and the back button works.
  // Cleanup plan: docs/plans/2026-04-25-accountant-queue-cleanup.md (BUG-3).
  const [searchParams, setSearchParams] = useSearchParams();
  const rawFilter = searchParams.get('filter');
  const statusFilter: StatusFilterValue =
    rawFilter && (VALID_STATUS_FILTERS as string[]).includes(rawFilter)
      ? (rawFilter as StatusFilterValue)
      : 'all';
  const setStatusFilter = useCallback(
    (next: StatusFilterValue) => {
      const params = new URLSearchParams(searchParams);
      if (next === 'all') params.delete('filter');
      else params.set('filter', next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<QueueResponse>(
        `/api/v1/queue/accountant/available?page=${page}&page_size=20`,
      );
      setCases(Array.isArray(data.cases) ? data.cases : []);
      setTotalCount(data.total_count || 0);
      setTotalPages(data.total_pages || 1);
      setError(null);
    } catch (err: any) {
      // Fallback to legacy endpoint
      try {
        const data = await api.get<QueueResponse>(
          `/api/v1/queue/accountant?page=${page}&page_size=20`,
        );
        setCases(Array.isArray(data.cases) ? data.cases : []);
        setTotalCount(data.total_count || 0);
        setTotalPages(data.total_pages || 1);
        setError(null);
      } catch (fallbackErr: any) {
        setError(fallbackErr.message || 'Failed to load queue');
      }
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Notify parent which IDs are currently on screen so it can clear
  // selectedCaseId if the user tabs to a view that doesn't contain it.
  // Use a ref to compare and avoid tight-loop updates.
  const lastNotifiedRef = useRef<string>('');
  useEffect(() => {
    const ids = cases.map((c) => c.id);
    const key = ids.join('|');
    if (key !== lastNotifiedRef.current) {
      lastNotifiedRef.current = key;
      onVisibleCaseIdsChange?.(ids);
    }
  }, [cases, onVisibleCaseIdsChange]);

  const handleClaim = async (e: React.MouseEvent, caseItem: CaseSummary) => {
    e.stopPropagation();
    setClaimingCase(caseItem.id);
    try {
      await api.post(`/api/v1/queue/accountant/claim/${caseItem.id}`, {});
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to claim case');
    } finally {
      setClaimingCase(null);
    }
  };

  const filteredCases = useCaseFilter(cases, searchQuery, statusFilter);

  return (
    <TabShell
      tabPanelId="tabpanel-queue"
      tabPanelLabelledBy="tab-queue"
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      statusFilter={statusFilter}
      onStatusFilterChange={setStatusFilter}
      heading="Available Cases"
      filteredCount={filteredCases.length}
      totalCount={totalCount}
      loading={loading}
      error={error}
      onDismissError={() => setError(null)}
      onRetry={fetchData}
      page={page}
      totalPages={totalPages}
      onPrevPage={() => setPage(Math.max(1, page - 1))}
      onNextPage={() => setPage(Math.min(totalPages, page + 1))}
      isEmpty={filteredCases.length === 0}
      emptyMessage={
        searchQuery || statusFilter !== 'all'
          ? 'No cases match your search criteria. Try adjusting your filters.'
          : 'No available cases to claim. All cases are being worked on!'
      }
    >
      {filteredCases.map((caseItem) => (
        <AccountantCaseRow
          key={caseItem.id}
          caseItem={caseItem}
          mode="queue"
          isSelected={selectedCaseId === caseItem.id}
          // Strict select (no toggle): clicking an already-selected row must
          // keep it selected. Side panel dismissal goes through the explicit
          // ✕ Close button on AccountantDashboard. See cleanup plan
          // BUG-4: docs/plans/2026-04-25-accountant-queue-cleanup.md
          onSelect={(id) => onSelectCase(id)}
          currentAccountantId={currentAccountantId}
          onClaim={handleClaim}
          claimingCaseId={claimingCase}
        />
      ))}
    </TabShell>
  );
}
